// client/src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

// Create a React Context for authentication
const AuthContext = createContext(null);

// Base URL for API calls, loaded from environment variables
const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;

// AuthProvider component that wraps the application and provides auth state/functions
export const AuthProvider = ({ children }) => {
  // State for the authenticated user, initialized from localStorage
  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('authToken');
      if (storedUser && storedToken) {
        // Basic validation: check if token is expired
        const decoded = jwtDecode(storedToken);
        if (decoded.exp * 1000 < Date.now()) { // Token expired
          // Clear expired data from localStorage
          localStorage.removeItem('user');
          localStorage.removeItem('authToken');
          return null; // No user if token is expired
        }
        return JSON.parse(storedUser); // Return parsed user data if valid
      }
    } catch (error) {
      console.error("Failed to parse user/token from localStorage", error);
      // Clear potentially corrupted data if parsing fails
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
    }
    return null; // No user initially
  });

  // State for the authentication token, initialized from localStorage
  const [token, setToken] = useState(() => localStorage.getItem('authToken') || null);

  const navigate = useNavigate(); // Hook for programmatic navigation

  // State for messages displayed on authentication pages (Login, Signup)
  const [authPageMessage, setAuthPageMessage] = useState('');
  const [authPageMessageType, setAuthPageMessageType] = useState('');

  // Memoized callback to set messages, ensuring stable reference
  const setPageMessage = useCallback((message, type) => {
    setAuthPageMessage(message);
    setAuthPageMessageType(type);
    // Messages for auth page will not time out here; parent component can clear them
  }, []);

  // Function to handle user login
  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_ROOT_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const result = await response.json(); // Parse response JSON

      if (response.ok) {
        // Set user state with data from successful login response
        const loggedInUser = {
          _id: result._id,
          username: result.username,
          email: result.email,
          role: result.role,
          associatedDealerId: result.associatedDealerId,
          accountId: result.accountId, // NEW: Store accountId
        };
        setUser(loggedInUser);
        setToken(result.token); // Set the JWT token

        // Store user data and token in localStorage for persistence
        localStorage.setItem('user', JSON.stringify(loggedInUser));
        localStorage.setItem('authToken', result.token);

        setPageMessage('', ''); // Clear any previous messages
        return { success: true }; // Indicate success
      } else {
        // Set error message if login failed
        setPageMessage(result.message || 'Login failed.', 'danger');
        return { success: false, message: result.message || 'Login failed.' };
      }
    } catch (error) {
      console.error('Login error:', error);
      setPageMessage('Network error or server unreachable during login.', 'danger');
      return { success: false, message: 'Network error or server unreachable.' };
    }
  };

  // Function to update user data in context and local storage without re-authenticating
  const updateUserInContext = useCallback((updatedUserData) => {
    setUser(prevUser => {
      const newUser = { ...prevUser, ...updatedUserData };
      localStorage.setItem('user', JSON.stringify(newUser));
      return newUser;
    });
  }, []);

  // Function to handle user registration (updated to use accountId)
  const register = async ({ username, email, password, accountId }) => {
    try {
      const response = await fetch(`${API_ROOT_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Send accountId along with other user details
        body: JSON.stringify({ username, email, password, accountId }),
      });
      const result = await response.json();

      if (response.ok) {
        // Set success message if registration was successful
        setPageMessage(result.message || 'Registration successful!', 'success');
        return { success: true, message: result.message || 'Registration successful!' };
      } else {
        // Set error message if registration failed
        setPageMessage(result.message || 'Registration failed.', 'danger');
        return { success: false, message: result.message || 'Registration failed.' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      setPageMessage('Network error or server unreachable during registration.', 'danger');
      return { success: false, message: 'Network error or server unreachable.' };
    }
  };

  // Function to handle user logout, memoized for stability
  const logout = useCallback(() => {
    setUser(null); // Clear user state
    setToken(null); // Clear token state
    localStorage.removeItem('user'); // Remove user data from localStorage
    localStorage.removeItem('authToken'); // Remove token from localStorage
    setPageMessage('You have been logged out.', 'info'); // Set logout message
    navigate('/login'); // Redirect to login page
  }, [navigate, setPageMessage]); // Dependencies for useCallback

  // Effect to handle token expiration.
  // Rather than a one-time check that only runs when `token` changes in React
  // state, we decode the expiry timestamp and schedule a setTimeout for the
  // exact millisecond the token becomes invalid. This means the redirect fires
  // even if the user has left the tab open and idle for hours.
  useEffect(() => {
    if (!token) return;

    let timeoutId;
    try {
      const decoded = jwtDecode(token);
      const msUntilExpiry = decoded.exp * 1000 - Date.now();

      if (msUntilExpiry <= 0) {
        // Already expired — e.g. page was refreshed after the token expired.
        // (The useState initializer above normally catches this on hard refresh,
        // but this handles the edge case where the clock drifts mid-session.)
        logout();
        setPageMessage('Your session has expired. Please log in again.', 'warning');
      } else {
        // Schedule the logout to fire at the exact expiry moment.
        timeoutId = setTimeout(() => {
          logout();
          setPageMessage('Your session has expired. Please log in again.', 'warning');
        }, msUntilExpiry);
      }
    } catch (error) {
      console.error('Invalid token, logging out:', error);
      logout();
      setPageMessage('Invalid session token. Please log in again.', 'warning');
    }

    // Cleanup: cancel the timer if the token changes (e.g. user logs in again
    // with a fresh token) or if the provider unmounts.
    return () => clearTimeout(timeoutId);
  }, [token, logout, setPageMessage]);

  // Value provided by the AuthContext to its consumers
  const authContextValue = {
    user, // Current authenticated user object
    token, // Current JWT token
    login, // Function to log in
    logout, // Function to log out
    register, // Function to register
    updateUserInContext, // NEW: Function to update user data in context
    isAuthenticated: !!user, // Convenience boolean: true if user is logged in
    isAdmin: user && user.role === 'Admin', // Example role check: true if user is Admin
    isManager: user && ['Admin', 'PreProduction', 'Manufacturing', 'Shipping'].includes(user.role), // Check if user is any type of manager
    isDealer: user && user.role === 'Dealer', // Check if user is a Dealer
    authPageMessage, // Message to display on auth pages
    authPageMessageType, // Type of message (e.g., 'success', 'danger')
    setAuthPageMessage: setPageMessage, // Expose the setter for message
  };

  return (
    // Provide the auth context value to all children components
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to consume the AuthContext easily in functional components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider'); // Ensure hook is used within provider
  }
  return context;
};